import { formatDay, formatDayRange, wallClockFromInstant } from '@mononoke/shared';

/*
 * ⚠️ ITS OWN MODULE FOR THE BOOT BUDGET (0.1.526). `rangeLabel` lived in `booking-display.tsx`, and
 * two EAGER modules (`bulk-edit.tsx`, through the Items tab, and the outfit clash line) import it —
 * which put the whole reservations/check-outs display spec on the app's boot path. The 0.1.526 Mail
 * column added under a kilobyte there and the boot path read 512,040 bytes, 40 over the 500 kB
 * ceiling (main sat 416 bytes under it — 0.1.460's warning). Here, the boot path carries this one
 * function and the display spec loads with the screens that use it. Rejected: raising the ceiling
 * (the budget governs the app, 0.1.459) and moving the Mail code out (the next addition there
 * would trip it again — the import edge was the cause).
 */

/**
 * How a booking's range READS. Whole-day bookings keep the old sentence exactly —
 * "2036-05-10 – 2036-05-14" — because that is what they still mean and 54 e2e
 * assertions depend on it. A timed booking shows its clock: same day collapses to
 * "2036-05-10 16:15 – 17:00", across days it names both ends in full.
 *
 * The end shown is the INCLUSIVE last day (endsOn, 015's projection) for whole-day
 * ranges and the EXCLUSIVE instant for timed ones — which is what a person means by
 * "back by five" and avoids ever printing "– 15th 00:00" for a booking that ends on
 * the 14th.
 */
export function rangeLabel(b: {
  startsAt: string | null;
  endsAt: string | null;
  startsOn: string | null;
  endsOn: string | null;
  wholeDay: boolean;
}): string {
  // ⚠️ OPEN BOUNDS (091): dates are optional. Said in words — never a date nobody chose, and never
  // "1 Jan 1970", which is what `new Date(null)` would have printed.
  if (b.startsOn === null && b.endsOn === null) return 'No dates yet';
  if (b.startsOn === null || b.endsOn === null) {
    const side = (on: string, at: string | null): string =>
      b.wholeDay || !at ? formatDay(on) : `${formatDay(on)} ${wallClockFromInstant(new Date(at)).split('T')[1] ?? ''}`;
    return b.startsOn === null ? `Until ${side(b.endsOn!, b.endsAt)}` : `From ${side(b.startsOn, b.startsAt)}`;
  }
  // Whole-day → the collapsed human range the frames draw ("14–16 Aug 2026"), through the
  // shared formatter. Timed → the day is formatted the same way, the clock left as-is. (A caller
  // that has only the days — the outfit clash line — passes no instants and is drawn as days.)
  if (b.wholeDay || !b.startsAt || !b.endsAt) return formatDayRange(b.startsOn, b.endsOn);
  const start = wallClockFromInstant(new Date(b.startsAt));
  const end = wallClockFromInstant(new Date(b.endsAt));
  const [startDay, startTime] = start.split('T') as [string, string];
  const [endDay, endTime] = end.split('T') as [string, string];
  return startDay === endDay
    ? `${formatDay(startDay)} ${startTime} – ${endTime}`
    : `${formatDay(startDay)} ${startTime} – ${formatDay(endDay)} ${endTime}`;
}
